import { all, fork, put, takeEvery, takeLatest, call, select } from "redux-saga/effects"
import * as actionTypes from "appRedux/actions/roomPlan/roomplanEventActionType"
import * as actionCreators from "appRedux/actions/roomPlan/roomplanEvent"
import { userSignOut } from "appRedux/actions/Auth"
import { API_URL_V1, API_PMS_HEADERS } from "constants/ApiSetting"
import { getItems } from "util/localStorage"
import { APIPostRequest } from "util/connection"
import { ApiResponse } from "constants/ActionTypes"
import { AppState } from "ReducerState"
import {
  buildRoomPlanQueryAuthScope,
  fetchRoomPlanQuery,
  invalidateRoomPlanQueries,
  isRoomPlanForceRefresh,
  stripRoomPlanInternalQueryFlags,
} from "services/api/roomPlanQuery"

const ROOM_STATUS_FILTERS = [
  ["statusVAC", "VAC"],
  ["statusOCC", "OCC"],
  ["statusOOO", "OOO"],
  ["statusOOS", "OOS"],
  ["statusOOI", "OOI"],
] as const;

const MAID_STATUS_FILTERS = [
  ["maidDirty", "DI"],
  ["maidIn", "MI"],
  ["maidClean", "CL"],
  ["maidInspected", "IS"],
  ["maidPickup", "PU"],
] as const;

const RESOURCE_ID_FILTERS = [
  "roomtype_id",
  "bedtype_id",
  "roomgroup_id",
  "building_id",
  "location_id",
  "exposure_id",
] as const;

const hasValue = (value: any) => value !== undefined && value !== null && value !== "";

const getActiveStatuses = (payload: any, entries: readonly (readonly [string, string])[]) =>
  entries
    .filter(([key]) => Boolean(payload?.[key]))
    .map(([, status]) => status);

const appendEventFilterParams = (qs: URLSearchParams, payload: any = {}) => {
  RESOURCE_ID_FILTERS.forEach((key) => {
    if (hasValue(payload[key])) qs.set(key, String(payload[key]));
  });

  const roomStatuses = getActiveStatuses(payload, ROOM_STATUS_FILTERS);
  const maidStatuses = getActiveStatuses(payload, MAID_STATUS_FILTERS);
  if (roomStatuses.length > 0) qs.set("statuses", roomStatuses.join(","));
  if (maidStatuses.length > 0) qs.set("hk_statuses", maidStatuses.join(","));
};

function* roomplanEventRequestProcess(
  { payload }: actionTypes.RoomPlanEventRequest
): Generator<any, void, any> {

  const { access_token } = yield call(getItems, ['access_token'])
  const AccessToken = access_token
  const authUser = yield select((state: AppState) => state.auth.authUser)
  const authScope = buildRoomPlanQueryAuthScope(authUser)

  API_PMS_HEADERS.headers.Authorization = `Bearer ${AccessToken}`
  const requestPayload = stripRoomPlanInternalQueryFlags(payload || {});
  const forceRefresh = isRoomPlanForceRefresh(payload);

  const {
    start_date,
    end_date,
    page = 1,
    pageSize = 2000,
    slim = true,
    fields,
    roomNumberSearch,
  } = (requestPayload as any) || {};

  // Build query string
  const qs = new URLSearchParams();
  qs.set("start_date", start_date);
  qs.set("end_date", end_date);
  qs.set("page", String(page));
  qs.set("pageSize", String(pageSize));
  if (slim) qs.set("slim", "true");
  if (Array.isArray(fields) && fields.length) qs.set("fields", fields.join(","));
  if (roomNumberSearch) qs.set("roomNumberSearch", String(roomNumberSearch));
  appendEventFilterParams(qs, requestPayload);

  const url = `${API_URL_V1}pms/inventory/roomPlan/event/?${qs.toString()}`;

  try {
    const response: ApiResponse = yield call(
      fetchRoomPlanQuery,
      "event",
      url,
      API_PMS_HEADERS,
      forceRefresh,
      authScope
    )
    if (response.status === 200) {
      const events = Array.isArray(response.data) ? response.data : [];
      yield put(actionCreators.roomplanEventDatasouceSuccess(events));

    } else {
      const { message, data } = response.data
      const processStatus = { status: false, action: "roomplanGetEvent", message }
      yield put(actionCreators.roomplanEventDatasouceSuccess([]))
      yield put(actionCreators.roomplanEventProcessSuccess(processStatus))
      yield put(actionCreators.roomplanEventProcessReset())
      if (response.status === 403) {
        if (data) yield put(userSignOut())
      }
    }
  } catch (error) {
    const processStatus = { status: false, action: "roomplanGetEvent", message: error }
    yield put(actionCreators.roomplanEventProcessSuccess(processStatus))
    yield put(actionCreators.roomplanEventProcessReset())
  }
}

function* roomplanEventInhouseChangeDateProcess({ payload }: actionTypes.RoomPlanEventDragInhouseChangeDate) {
  const { register_id, new_departure } = payload
  const { access_token } = yield call(getItems, ['access_token'])
  const AccessToken = access_token
  const url = `${API_URL_V1}pms/registration/guestInHouse/${register_id}/changeDeparture/`

  API_PMS_HEADERS.headers.Authorization = `Bearer ${AccessToken}`
  const API_DATABODY = { new_departure }
  try {
    const response: ApiResponse = yield APIPostRequest(url, API_DATABODY, API_PMS_HEADERS)
    if (response.status === 200) {
      const { result_data, result_code, result_msg } = response.data
      const processStatus = { status: true, action: "eventDragInhouseChangeDate", message: result_msg, result_code, result_data }
      yield call(invalidateRoomPlanQueries)
      yield put(actionCreators.roomplanEventProcessSuccess(processStatus))

    } else {
      /* Show error message and signout */
      const { message, data } = response.data
      const processStatus = { status: false, action: "eventDragInhouseChangeDate", message }
      yield put(actionCreators.roomplanEventProcessSuccess(processStatus))
      if (response.status === 403) {
        //if data is not null force signout 
        if (data)
          yield put(userSignOut())
      }

    }
  } catch (error) {
    const processStatus = { status: false, action: "eventDragInhouseChangeDate", message: error }
    yield put(actionCreators.roomplanEventProcessSuccess(processStatus))
  }
}

function* roomplanEventInhouseChangeRoomProcess({ payload }: actionTypes.RoomPlanEventDragInhouseChangeRoom) {
  const { register_id, new_room_id } = payload
  const { access_token } = yield call(getItems, ['access_token'])
  const AccessToken = access_token
  const url = `${API_URL_V1}pms/registration/guestInHouse/${register_id}/changeRoom/`

  API_PMS_HEADERS.headers.Authorization = `Bearer ${AccessToken}`
  const API_DATABODY = { new_room_id }
  try {
    const response: ApiResponse = yield APIPostRequest(url, API_DATABODY, API_PMS_HEADERS)
    if (response.status === 200) {
      const { result_data, result_code, result_msg } = response.data
      const processStatus = { status: true, action: "eventDragInhouseChangeRoom", message: result_msg, result_code, result_data }
      yield call(invalidateRoomPlanQueries)
      yield put(actionCreators.roomplanEventProcessSuccess(processStatus))

    } else {
      /* Show error message and signout */
      const { message, data } = response.data
      const processStatus = { status: false, action: "eventDragInhouseChangeRoom", message }
      yield put(actionCreators.roomplanEventProcessSuccess(processStatus))
      if (response.status === 403) {
        //if data is not null force signout 
        if (data)
          yield put(userSignOut())
      }

    }
  } catch (error) {
    const processStatus = { status: false, action: "eventDragInhouseChangeRoom", message: error }
    yield put(actionCreators.roomplanEventProcessSuccess(processStatus))
  }
}

function* roomplanEventOOOChangeDateProcess({ payload }: actionTypes.RoomPlanEventDragOOOChangeDate) {

  const { access_token } = yield call(getItems, ['access_token'])
  const AccessToken = access_token
  const url = `${API_URL_V1}pms/housekeeping/roomStatus/changeOutOfOrderPeriod/`

  const oooChangeInfo = {} as any
  oooChangeInfo.ooo_id = payload.ooo_id
  oooChangeInfo.new_start = payload.new_start
  oooChangeInfo.new_end = payload.new_end

  API_PMS_HEADERS.headers.Authorization = `Bearer ${AccessToken}`
  const API_DATABODY = oooChangeInfo
  try {
    const response: ApiResponse = yield APIPostRequest(url, API_DATABODY, API_PMS_HEADERS)
    if (response.status === 200) {
      const { result_data, result_code, result_msg } = response.data
      const processStatus = { status: true, action: "eventDragOOOChangeDate", message: result_msg, result_code, result_data }
      yield call(invalidateRoomPlanQueries)
      yield put(actionCreators.roomplanEventProcessSuccess(processStatus))

    } else {
      /* Show error message and signout */
      const { message, data } = response.data
      const processStatus = { status: false, action: "eventDragOOOChangeDate", message }
      yield put(actionCreators.roomplanEventProcessSuccess(processStatus))
      if (response.status === 403) {
        //if data is not null force signout 
        if (data)
          yield put(userSignOut())
      }

    }
  } catch (error) {
    const processStatus = { status: false, action: "eventDragOOOChangeDate", message: error }
    yield put(actionCreators.roomplanEventProcessSuccess(processStatus))
  }
}
function* roomplanEventBlockChangeRoomProcess({ payload }: actionTypes.RoomPlanEventDragBlockChangeRoom) {

  const { access_token } = yield call(getItems, ['access_token'])
  const AccessToken = access_token
  const url = `${API_URL_V1}pms/reservation/blockRoom/changeRoom/`

  const BlockChangeInfo = {} as any
  BlockChangeInfo.block_id = payload.block_id
  BlockChangeInfo.new_room_id = payload.new_room_id

  API_PMS_HEADERS.headers.Authorization = `Bearer ${AccessToken}`
  const API_DATABODY = BlockChangeInfo
  try {
    const response: ApiResponse = yield APIPostRequest(url, API_DATABODY, API_PMS_HEADERS)
    if (response.status === 200) {
      const { result_data, result_code, result_msg } = response.data
      const processStatus = { status: true, action: "eventDragBlockChangeRoom", message: result_msg, result_code, result_data }
      yield call(invalidateRoomPlanQueries)
      yield put(actionCreators.roomplanEventProcessSuccess(processStatus))

    } else {
      /* Show error message and signout */
      const { message, data } = response.data
      const processStatus = { status: false, action: "eventDragBlockChangeRoom", message }
      yield put(actionCreators.roomplanEventProcessSuccess(processStatus))
      if (response.status === 403) {
        //if data is not null force signout 
        if (data)
          yield put(userSignOut())
      }

    }
  } catch (error) {
    const processStatus = { status: false, action: "eventDragBlockChangeRoom", message: error }
    yield put(actionCreators.roomplanEventProcessSuccess(processStatus))
  }
}

function* roomplanEventRequest() {
  yield takeLatest(actionTypes.ROOMPLAN_EVENT_REQUEST, roomplanEventRequestProcess)
}
function* roomplanEventInhouseChangeDate() {
  yield takeEvery(actionTypes.ROOMPLAN_EVENT_DRAG_INHOUSE_CHANGE_DATE, roomplanEventInhouseChangeDateProcess)
}
function* roomplanEventInhouseChangeRoom() {
  yield takeEvery(actionTypes.ROOMPLAN_EVENT_DRAG_INHOUSE_CHANGE_ROOM, roomplanEventInhouseChangeRoomProcess)
}
function* roomplanEventOOOChangeDate() {
  yield takeEvery(actionTypes.ROOMPLAN_EVENT_DRAG_OOO_CHANGE_DATE, roomplanEventOOOChangeDateProcess)
}
function* roomplanEventBlockChangeRoom() {
  yield takeEvery(actionTypes.ROOMPLAN_EVENT_DRAG_BLOCK_CHANGE_ROOM, roomplanEventBlockChangeRoomProcess)
}


export default function* rootSaga() {
  yield all([fork(roomplanEventRequest),
  fork(roomplanEventInhouseChangeDate),
  fork(roomplanEventOOOChangeDate),
  fork(roomplanEventInhouseChangeRoom),
  fork(roomplanEventBlockChangeRoom)
  ])
}
